import {
  ComponentPublicInstance,
  computed,
  DeepReadonly,
  readonly,
  Ref,
  ref,
  watch,
} from 'vue'

import { IncidentId } from '@collector/sportsapi-client-legacy'
import { ProbableLinkedIncident } from '@mobile/views/Relation/Shared/RelationDependencies/types'
import { EventInjections } from '@mobile/views/Relation/Shared/RelationDependencies/useEventInjections'

import { useHistoryIncidents } from './useHistoryIncidents'

export type DockedSelectFocus = 'main' | 'secondary' | 'ternary'

export type DockedIncidentPosition = {
  top: number
  left: number
}

type DockSnapshot = {
  position: DockedIncidentPosition
  shouldFocusOnMinutesInput: boolean
  openedSelect: DockedSelectFocus | null
}

type ScrollPosition = {
  scrollX: number
  scrollY: number
}

export type DockedIncidentComponentReturnType = {
  position: DeepReadonly<Ref<DockedIncidentPosition | null>>
  shouldFocusOnMinutesInput: DeepReadonly<Ref<boolean>>
  openedSelect: DeepReadonly<Ref<DockedSelectFocus | null>>
  openedMainSelect: DeepReadonly<Ref<boolean>>
  openedSecondarySelect: DeepReadonly<Ref<boolean>>
  openedTernarySelect: DeepReadonly<Ref<boolean>>
  dockedLinkedIncident: DeepReadonly<Ref<ProbableLinkedIncident | null>>
  isDocked: DeepReadonly<Ref<boolean>>
  setInitialDockPositionFromRow: (
    linkedIncident: ProbableLinkedIncident,
    row: ComponentPublicInstance | null,
  ) => void
  handleRowClick: (
    linkedIncident: ProbableLinkedIncident,
    event: PointerEvent,
    dockable: boolean,
  ) => void
  shouldStopRowClickPropagation: (event: PointerEvent) => boolean
  requestVIPIncidentDock: (
    incidentId: IncidentId,
    participantId: number,
  ) => void
}

const position: Ref<DockedIncidentPosition | null> = ref(null)
const shouldFocusOnMinutesInput = ref(false)
const openedSelect: Ref<DockedSelectFocus | null> = ref(null)
let pendingScrollRestore: ScrollPosition | null = null

export function requestDockFocusOnMinutesInput(): void {
  shouldFocusOnMinutesInput.value = true
}

export function requestDockFocusOnMainSelect(): void {
  openedSelect.value = 'main'
}

export function clearDockFocusState(): void {
  shouldFocusOnMinutesInput.value = false
  openedSelect.value = null
  position.value = null
  pendingScrollRestore = null
}

function detectSelectFocus(
  element: HTMLElement | null,
): DockedSelectFocus | null {
  if (!element) {
    return null
  }

  const selectElement =
    element.closest('[data-participant-select]') ??
    element.closest('.multiselect')?.closest('[data-participant-select]')

  if (!selectElement) {
    return null
  }

  const selectType = selectElement.getAttribute('data-participant-select')
  if (
    selectType === 'main' ||
    selectType === 'secondary' ||
    selectType === 'ternary'
  ) {
    return selectType
  }

  return null
}

function getElementFromRowRef(
  row: ComponentPublicInstance | null,
): HTMLElement | null {
  if (!row) {
    return null
  }

  return row.$el instanceof HTMLElement ? row.$el : null
}

function applyDockSnapshot(snapshot: DockSnapshot): void {
  position.value = snapshot.position
  shouldFocusOnMinutesInput.value = snapshot.shouldFocusOnMinutesInput
  openedSelect.value = snapshot.openedSelect
}

function setPositionFromElement(element: HTMLElement): void {
  position.value = {
    top: element.offsetTop,
    left: element.offsetLeft,
  }

  if (pendingScrollRestore) {
    restoreScrollPosition(
      pendingScrollRestore.scrollX,
      pendingScrollRestore.scrollY,
    )
    pendingScrollRestore = null
  }
}

function restoreScrollPosition(scrollX: number, scrollY: number): void {
  requestAnimationFrame(() => {
    window.scrollTo(scrollX, scrollY)
  })
}

function getRowElementFromClick(event: PointerEvent): HTMLElement | null {
  if (event.currentTarget instanceof HTMLElement) {
    return event.currentTarget
  }

  const target = event.target
  if (!(target instanceof HTMLElement)) {
    return null
  }

  const row = target.closest('.table-row')
  return row instanceof HTMLElement ? row : null
}

function isRowClickDockIgnoredTarget(element: HTMLElement | null): boolean {
  if (!element) {
    return false
  }

  return (
    element.closest('.multiselect-option') !== null ||
    element.closest('[data-incident-pin-button]') !== null
  )
}

function shouldStopRowClickPropagation(event: PointerEvent): boolean {
  const element = event.target as HTMLElement | null
  if (!element) {
    return false
  }

  return (
    element.closest('.multiselect-option') !== null ||
    element.closest('[data-participant-select]') !== null ||
    element.closest('.multiselect') !== null
  )
}

function createSnapshotFromClick(event: PointerEvent): DockSnapshot | null {
  const rowElement = getRowElementFromClick(event)
  if (!rowElement) {
    return null
  }

  const element = event.target as HTMLElement | null

  return {
    position: {
      top: rowElement.offsetTop,
      left: rowElement.offsetLeft,
    },
    shouldFocusOnMinutesInput:
      element?.closest('.incident-time-update-button') !== null,
    openedSelect: detectSelectFocus(element),
  }
}

export function useDockedIncidentComponent(
  eventInjections: EventInjections,
): DockedIncidentComponentReturnType {
  const {
    selectIncident,
    isIncidentSelected,
    selectedIncident,
    requestVIPIncidentDock: requestVIPIncidentDockFromHistory,
  } = useHistoryIncidents(eventInjections)

  const openedMainSelect = computed(() => openedSelect.value === 'main')
  const openedSecondarySelect = computed(
    () => openedSelect.value === 'secondary',
  )
  const openedTernarySelect = computed(() => openedSelect.value === 'ternary')

  watch(
    selectedIncident,
    (incident) => {
      if (!incident) {
        clearDockFocusState()
      }
    },
    { flush: 'sync' },
  )

  function setInitialDockPositionFromRow(
    linkedIncident: ProbableLinkedIncident,
    row: ComponentPublicInstance | null,
  ): void {
    if (!isIncidentSelected(linkedIncident) || position.value !== null) {
      return
    }

    const element = getElementFromRowRef(row)
    if (!element) {
      return
    }

    setPositionFromElement(element)
  }

  function handleRowClick(
    linkedIncident: ProbableLinkedIncident,
    event: PointerEvent,
    dockable: boolean,
  ): void {
    if (!dockable) {
      return
    }

    const element = event.target as HTMLElement | null
    if (isRowClickDockIgnoredTarget(element)) {
      return
    }

    if (isIncidentSelected(linkedIncident)) {
      return
    }

    const snapshot = createSnapshotFromClick(event)
    if (!snapshot) {
      return
    }

    const scrollX = window.scrollX
    const scrollY = window.scrollY

    applyDockSnapshot(snapshot)
    selectIncident(linkedIncident)
    restoreScrollPosition(scrollX, scrollY)
  }

  function requestVIPIncidentDock(
    incidentId: IncidentId,
    participantId: number,
  ): void {
    pendingScrollRestore = {
      scrollX: window.scrollX,
      scrollY: window.scrollY,
    }
    requestVIPIncidentDockFromHistory(incidentId, participantId)
  }

  return {
    position: readonly(position),
    shouldFocusOnMinutesInput: readonly(shouldFocusOnMinutesInput),
    openedSelect: readonly(openedSelect),
    openedMainSelect: readonly(openedMainSelect),
    openedSecondarySelect: readonly(openedSecondarySelect),
    openedTernarySelect: readonly(openedTernarySelect),
    dockedLinkedIncident: selectedIncident,
    isDocked: readonly(computed(() => selectedIncident.value !== null)),
    setInitialDockPositionFromRow,
    handleRowClick,
    shouldStopRowClickPropagation,
    requestVIPIncidentDock,
  }
}
